// Cruise Command · Commercial Launch Readiness · shared SSO Providers
//
// Extracted from Login.jsx so the same three federated buttons can
// appear on:
//   · the Marketing Landing (above the fold — public entry point)
//   · the LoginPage (the dedicated sign-in surface)
//
// Contract: identical UX to Login's SignInCard. If the visitor is
// already authenticated the parent should not render this component.
//
// Providers:
//   • Google (Emergent-managed OAuth · PD-028 · full-page redirect)
//   • Apple  (Sign in with Apple JS SDK · popup · id_token flow)
//   • Microsoft (MSAL for Entra ID · full-page redirect · AUTH-MS-003 Track 1)
import React, { useCallback, useState } from "react";
import { ArrowRight, Loader2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { isNativePlatform } from "@/lib/platform";
import {
  generateNonce,
  signInWithApple,
  appleDisplayName,
  initiateMicrosoftRedirect,
  describeSsoBackendError,
  nativeSessionNotEstablishedError,
  FederatedAuthError,
} from "@/lib/federatedAuth";
import { launchNativeGoogleBroker } from "@/lib/mobile/nativeGoogleBroker";
import { useAuthOptional } from "@/context/AuthContext";

export function SsoProviders({ onSignedIn, appearance = "light", compact = false }) {
  const [busy, setBusy] = useState(null);   // "google" | "apple" | "microsoft" | null
  const [error, setError] = useState(null); // { title, message, code }
  // Web Google/Microsoft return through their callback routes (which hydrate
  // AuthContext themselves). Apple and — since TKT #274563 — native Google
  // complete in-place here, so hydrate via AuthContext. MarketingLanding (the
  // `!user` surface) mounts this without `onSignedIn`, so fall back to refreshMe.
  const auth = useAuthOptional();
  const finalizeSignIn = onSignedIn ?? auth?.refreshMe;

  const handleGoogle = async () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (isNativePlatform()) {
      // TKT #274563 · DIRECT native Google: the SDK returns an id_token in-process
      // and launchNativeGoogleBroker() exchanges it inline (no browser, no deep
      // link, no app relaunch). We MUST then hydrate AuthContext so the shell
      // renders immediately; a null /me means the session did not establish and
      // must surface a visible error rather than a silent no-op.
      setBusy("google"); setError(null);
      try {
        const result = await launchNativeGoogleBroker();
        if (!result) return;  // overlap/no-op: another in-flight flow owns it
        const me = await finalizeSignIn?.();
        if (me === null) setError(nativeSessionNotEstablishedError("google"));
      } catch (err) {
        if (err instanceof FederatedAuthError) {
          setError({ title: "Sign-in could not be completed", message: err.message, code: err.code });
        } else {
          setError(describeSsoBackendError(err));
        }
      } finally {
        setBusy(null);
      }
      return;
    }
    const redirectUrl = window.location.origin + "/";
    window.location.href =
      "https://auth.emergentagent.com/?redirect=" + encodeURIComponent(redirectUrl);
  };

  const handleFederated = useCallback(async (provider) => {
    setBusy(provider); setError(null);
    try {
      const nonce = generateNonce();

      // Microsoft uses full-page redirect. Execution never returns
      // from this branch on success — the browser navigates to Entra
      // and lands back at <MicrosoftAuthCallback>.
      if (provider === "microsoft") {
        try {
          await initiateMicrosoftRedirect({ nonce });
          // Web: the page is navigating away. Native: the system browser is
          // up and this screen stays mounted — release the button so a
          // cancelled attempt can be retried.
          setBusy(null);
          return; // browser is navigating
        } catch (err) {
          if (err instanceof FederatedAuthError) {
            setError({ title: "Identity provider error", message: err.message, code: err.code });
          } else {
            setError(describeSsoBackendError(err));
          }
          setBusy(null);
          return;
        }
      }

      // Apple — popup id_token flow → open-registration signin.
      const idpResult = await signInWithApple({ nonce });
      if (!idpResult.native) {
        // Web only. Native already holds the bearer pair from the mobile exchange.
        await api.post(
          `/api/auth/sso/${provider}/signin`,
          { provider_token: idpResult.id_token, nonce, display_name: appleDisplayName(idpResult.user) },
        );
      }
      const me = await finalizeSignIn?.();
      if (me === null) {
        // Sign-in completed server-side but /api/auth/me did not hydrate
        // (refreshMe swallows 401s and returns null). Never stay silent.
        setError(nativeSessionNotEstablishedError("apple"));
      }
    } catch (err) {
      if (err instanceof FederatedAuthError) {
        if (err.code === "apple_not_completed") {
          setError({ title: "Apple sign-in did not complete", message: err.message, code: err.code });
          return;
        }
        if (err.code === "apple_session_not_established") {
          setError({ title: "Session could not be saved", message: err.message, code: err.code });
          return;
        }
        if (err.code === "apple_popup_closed" || err.code === "microsoft_popup_closed") {
          setError({ title: "Sign-in cancelled", message: "You closed the sign-in window before finishing.", code: err.code });
        } else {
          setError({ title: "Identity provider error", message: err.message, code: err.code });
        }
      } else {
        setError(describeSsoBackendError(err));
      }
    } finally {
      // Microsoft return path already navigated away.
      if (provider !== "microsoft") setBusy(null);
    }
  }, [finalizeSignIn]);

  return (
    <>
      <div className={compact ? "space-y-2" : "space-y-3"}
           data-testid="cc-auth-providers">
        <ProviderButton
          testId="cc-auth-google"
          glyph={<GoogleGlyph className="w-[18px] h-[18px]" />}
          label="Continue with Google"
          onClick={handleGoogle}
          busy={busy === "google"}
          disabled={busy !== null}
          compact={compact}
        />
        {/* Apple sign-in restored 2026-10-03 (hidden 2026-08-20 pending E2E). */}
        <ProviderButton
          testId="cc-auth-apple"
          glyph={<AppleGlyph className="w-[18px] h-[18px]" />}
          label="Continue with Apple"
          onClick={() => handleFederated("apple")}
          busy={busy === "apple"}
          disabled={busy !== null}
          compact={compact}
        />
        <ProviderButton
          testId="cc-auth-microsoft"
          glyph={<MicrosoftGlyph className="w-[16px] h-[16px]" />}
          label="Continue with Microsoft"
          onClick={() => handleFederated("microsoft")}
          busy={busy === "microsoft"}
          disabled={busy !== null}
          compact={compact}
        />
      </div>

      {error && (
        <div
          data-testid="cc-auth-sso-error"
          data-error-code={error.code || "unknown"}
          className={`mt-4 rounded-2xl px-5 py-4 ${appearance === "dark" ? "bg-red-500/[0.08] border border-red-500/25" : "bg-rose-50 border border-rose-200"}`}
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${appearance === "dark" ? "text-red-400" : "text-rose-600"}`} strokeWidth={2.4} />
            <div className="min-w-0">
              <div className={`text-[12.5px] font-bold ${appearance === "dark" ? "text-red-200" : "text-rose-800"}`}>{error.title}</div>
              <p className={`mt-1 text-[12.5px] leading-relaxed ${appearance === "dark" ? "text-red-100/80" : "text-rose-700"}`}>{error.message}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ProviderButton({ testId, glyph, label, onClick, busy, disabled, compact }) {
  const height = compact ? "h-11" : "h-[52px]";
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      data-busy={busy ? "true" : "false"}
      disabled={disabled}
      className={
        `group w-full rounded-2xl ${height} text-[14.5px] font-semibold text-slate-900 bg-white inline-flex items-center justify-between px-5 transition-all ` +
        (disabled && !busy
          ? "opacity-70 cursor-wait"
          : "hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(0,0,0,0.28)] shadow-[0_8px_22px_rgba(0,0,0,0.16)]")
      }
    >
      <span className="inline-flex items-center gap-3">
        <span>{glyph}</span>
        <span>{label}</span>
      </span>
      {busy ? (
        <Loader2 className="w-4 h-4 animate-spin text-slate-500" />
      ) : (
        <ArrowRight className="w-4 h-4 text-slate-500 group-hover:translate-x-0.5 transition-transform" />
      )}
    </button>
  );
}

function GoogleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9.1 3.5l6.8-6.8C35.6 2.3 30.2 0 24 0 14.6 0 6.5 5.4 2.6 13.3l7.9 6.1C12.4 13.5 17.7 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.5 24.6c0-1.7-.2-3.4-.5-5H24v9.5h12.7c-.6 3-2.3 5.5-4.9 7.2l7.6 5.9c4.4-4.1 7.1-10.1 7.1-17.6z"/>
      <path fill="#FBBC05" d="M10.5 28.6c-.5-1.4-.8-2.9-.8-4.6s.3-3.2.8-4.6L2.6 13.3C.9 16.6 0 20.2 0 24s.9 7.4 2.6 10.7l7.9-6.1z"/>
      <path fill="#34A853" d="M24 48c6.5 0 11.9-2.2 15.9-5.8l-7.6-5.9c-2.1 1.4-4.8 2.2-8.3 2.2-6.3 0-11.6-4-13.5-9.9l-7.9 6.1C6.5 42.6 14.6 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

function AppleGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#111" d="M17.564 12.63c-.02-2.213 1.812-3.281 1.895-3.334-1.034-1.51-2.64-1.717-3.213-1.742-1.367-.138-2.669.807-3.365.807-.696 0-1.767-.786-2.907-.764-1.495.022-2.874.87-3.643 2.207-1.552 2.69-.398 6.673 1.115 8.855.741 1.07 1.622 2.27 2.775 2.226 1.117-.046 1.539-.72 2.887-.72 1.347 0 1.727.72 2.907.694 1.2-.02 1.96-1.09 2.694-2.163.849-1.24 1.198-2.442 1.219-2.505-.027-.011-2.34-.898-2.364-3.561zm-2.24-6.539c.615-.746 1.03-1.782.917-2.815-.886.035-1.96.591-2.596 1.337-.57.66-1.07 1.716-.936 2.73.988.076 1.999-.503 2.615-1.252z" />
    </svg>
  );
}

function MicrosoftGlyph({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="13" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="13" width="10" height="10" fill="#00A4EF" />
      <rect x="13" y="13" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}

export default SsoProviders;
